'use client'

import { Builder, withChildren } from '@builder.io/react'
import type { BuilderElement, Component } from '@builder.io/sdk'

import type {
  BuilderComponentConfig,
  BuilderComponentProps,
  DeepReadonly,
} from '@src/typings/builder'
import { getOnChangeImageHandler, spacingBuilderInputs } from '@src/utils/builder.util'
import { theme } from '@src/utils/tailwind-theme'

import { HeroBanner } from './HeroBanner'
import { HeroLabels } from './HeroLabels'

const defaultTitle: BuilderElement = {
  '@type': '@builder.io/sdk:Element',
  component: { name: 'Text', options: { text: 'Type the Title...', style: 'Display Heading 1' } },
}
const defaultSubtitle: BuilderElement = {
  '@type': '@builder.io/sdk:Element',
  component: {
    name: 'Text',
    options: {
      text: 'Type the Subtitle...',
      style: 'Body MD',
      className: 'text-left',
    },
  },
}

export const defaultHeader = [
  {
    headerItem: [defaultTitle],
  },
  {
    headerItem: [defaultSubtitle],
  },
]

const defaultButton: BuilderElement = {
  '@type': '@builder.io/sdk:Element',
  children: [
    {
      '@type': '@builder.io/sdk:Element',
      component: { name: 'Text', options: { text: 'CTA BUTTON' } },
    },
  ],
  component: {
    name: 'Button',
    options: {
      size: 'medium',
    },
  },
}

const HeroLabelsComponent = {
  name: 'HeroLabels',
  inputs: [
    {
      name: 'logoLandscape',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg'],
    },
    {
      name: 'logoPortrait',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg'],
    },
    {
      name: 'headerItems',
      type: 'list',
      onChange: (options: any) => {
        const headerItems = options?.get('headerItems')
        const headerItem = headerItems[headerItems.length - 1 || 0]?.get('headerItem')[0]
        if (headerItems?.length > 1) {
          headerItem?.component.options.set('style', 'Body MD')
          headerItem?.component.options.set('text', 'Type the Subtitle...')
        } else {
          headerItem?.component.options.set('style', 'Display Heading 1')
        }
      },
      subFields: [
        {
          name: 'headerItem',
          type: 'uiBlocks',
          defaultValue: [defaultTitle],
        },
      ],
      defaultValue: [
        {
          headerItem: [defaultTitle],
        },
        {
          headerItem: [defaultSubtitle],
        },
      ],
    },
    {
      name: 'ctaItems',
      type: 'list',
      friendlyName: 'CTA Items',
      subFields: [
        {
          name: 'ctaItem',
          type: 'uiBlocks',
        },
      ],
      defaultValue: [
        {
          ctaItem: [defaultButton],
        },
      ],
    },
    {
      name: 'textVariantLandscape',
      type: 'string',
      enum: ['dark', 'light'],
      defaultValue: 'dark',
    },
    {
      name: 'textVariantPortrait',
      type: 'string',
      enum: ['dark', 'light'],
      defaultValue: 'light',
    },
  ],
  image: 'https://unpkg.com/@tabler/icons@2.44.0/icons/align-box-left-middle.svg',
} as const satisfies DeepReadonly<Component>

export type HeroLabelInputProps = BuilderComponentProps<typeof HeroLabelsComponent.inputs>
const HeroLabelsConfig = HeroLabelsComponent as BuilderComponentConfig<HeroLabelInputProps>

export const HeroBannerComponent = {
  name: 'HeroBanner',
  defaultStyles: {
    margin: '0',
  },
  inputs: [
    spacingBuilderInputs.outerSpacing,
    {
      name: 'backgroundColor',
      type: 'color',
      defaultValue: theme.colors['bodi-black'],
    },
    {
      name: 'mediaLandscape',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'mpg', 'mp2', 'mpeg', 'mpe', 'mpv', 'mp4', 'mov'],
      onChange: getOnChangeImageHandler({
        srcProperty: 'mediaLandscape',
        dataUriProperty: 'mediaLandscapeBlur',
      }),
    },
    {
      name: 'mediaPortrait',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'mpg', 'mp2', 'mpeg', 'mpe', 'mpv', 'mp4', 'mov'],
      onChange: getOnChangeImageHandler({
        srcProperty: 'mediaPortrait',
        dataUriProperty: 'mediaPortraitBlur',
      }),
    },
    {
      name: 'showDataURIs',
      friendlyName: 'Show Data URIs',
      helperText: 'Show the generated data URIs for image progressive loading.',
      type: 'boolean',
    },
    {
      name: 'mediaLandscapeBlur',
      type: 'text',
      showIf: (options) => !!options.get('showDataURIs'),
    },
    {
      name: 'mediaPortraitBlur',
      type: 'text',
      showIf: (options) => !!options.get('showDataURIs'),
    },
  ],
  image: 'https://unpkg.com/@tabler/icons@2.44.0/icons/photo-video.svg',
} as const satisfies DeepReadonly<Component>

export type HeroBannerInputProps = any
// TO-DO: Future refactor the banner component to use declarative props
const HeroBannerConfig = HeroBannerComponent as any

Builder.registerComponent(withChildren(HeroBanner), HeroBannerConfig)
Builder.registerComponent(withChildren(HeroLabels), HeroLabelsConfig)
